import { JSX, useContext, useEffect, useMemo, useState } from "react";

import { PageSection, SearchBox } from "@app/shared";
import { QESAlert, QESRadioGroup, QESRadioButton, QESCheckbox, QESSelect } from "@app/shared/qes-standards";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { TerminationsWizardStepProps, BulkProviderTerminationMethod } from "@app/terminations/import/interfaces";
import { BulkProviderChangeProjectTableData } from "@app/shared/provider-change-wizards/interfaces";
import { TerminationsWizardContext } from "@app/terminations/import/TerminationsWizardContext";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";

const ProjectsTableColumnConfig: ColumnConfig = {
  details: {
    displayText: "Details",
    testId: "details",
  },
  projectId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  projectName: {
    displayText: "Project Name",
    testId: "project-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  templateName: {
    displayText: "Template",
    testId: "template",
  },
};

const TerminationsWizardSelectProjects = (props: TerminationsWizardStepProps): JSX.Element => {
  const wizardContext = useContext(TerminationsWizardContext);

  const [projectSearchString, setProjectSearchString] = useState("");

  useEffect(() => {
    // fetch data from api
    wizardContext.providerTerminationService.getBulkTerminationProjects(wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost).then((response) => {
      const tabledata: BulkProviderChangeProjectTableData[] = response.map((project) => {
        const detailsArr: string[] = [];
        if (project.isReadOnly) {
          detailsArr.push("Read Only");
        }
        if (project.hasTerminations) {
          detailsArr.push("Existing Terminations");
        }
        if (!project.hasSuccessfulLatestRun && wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost) {
          detailsArr.push("Failed Latest Run");
        }
        const detailsStr = detailsArr.join(" and ");
        return {
          /* eslint-disable sort-keys */
          rowIsDisabled: project.isReadOnly || (!project.hasSuccessfulLatestRun && wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost),
          key: project.projectId.toString(),
          projectId: project.projectId,
          projectName: project.projectName,
          templateName: project.templateName,
          details: detailsStr,
          vaClaimsColumnEnabled: project.vaClaimsColumnEnabled,
          tricareClaimsColumnEnabled: project.tricareClaimsColumnEnabled,
          /* eslint-enable sort-keys */
        };
      });
      wizardContext.setProjects(tabledata);
    });
  }, []);

  useEffect(() => {
    if (!!wizardContext.selectedLineOfBusiness) {
      const updatedProjects = wizardContext.projects.map((project) => {
        if ((!project.vaClaimsColumnEnabled && wizardContext.selectedLineOfBusiness == "VA") || (!project.tricareClaimsColumnEnabled && wizardContext.selectedLineOfBusiness == "TRICARE")) {
          return {
            ...project,
            rowIsDisabled: true
          };
        } else {
          return {
            ...project,
            rowIsDisabled: !(project.details == "Existing Terminations" || project.details == "")
          };
        }
      });
      wizardContext.setProjects(updatedProjects);
      wizardContext.setSelectedProjectIds([]);
    }
  }, [wizardContext.selectedLineOfBusiness])

  const renderedProjects = useMemo(() => {
    if (!projectSearchString) {
      return wizardContext.projects.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
    }
    const regex = new RegExp(projectSearchString, "i");
    const filteredProjectList = wizardContext.projects.filter((project) => !!project.projectName.match(regex));
    return filteredProjectList.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
  }, [projectSearchString, wizardContext.projects]);

  const handleCheckboxChange = (selectedKeys: string[], isBulk?: boolean) => {
    // if the click is from the bulk checkbox, we need to make sure we only change _visible_ projects
    if (isBulk) {
      let newSelectedProjectIds = [...wizardContext.selectedProjectIds];
      // the bulk checkbox is all or nothing, so it's easy to tell if things are being added or removed
      if (selectedKeys.length > 0) {
        newSelectedProjectIds.push(...selectedKeys);
      } else {
        // iterate over the removedIds instead of newSelectedProjectIds,
        // because removing something from newSelectedProjectIds while iterating over it
        // is liable to cause issues
        const removedIds = renderedProjects.map((project) => project.projectId.toString());
        newSelectedProjectIds = newSelectedProjectIds.filter((id) => { return !removedIds.includes(id); });
      }
      wizardContext.setSelectedProjectIds([...new Set(newSelectedProjectIds)]);
    } else {
      wizardContext.setSelectedProjectIds(selectedKeys);
    }
  };

  useEffect(() => {
    if (wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost) {
      props.formIsValid(wizardContext.selectedProjectIds.length > 0 && !!wizardContext.selectedLineOfBusiness);
    } else {
      props.formIsValid(wizardContext.selectedProjectIds.length > 0)
    }
  }, [wizardContext.selectedProjectIds, wizardContext.selectedLineOfBusiness]);

  return (
    <PageSection>
      <div className="container mt-3">
        <p className="copy-text-default">Select the projects you would like to terminate from.</p>
        <QESAlert type="info" classNames="mb-2" persist={true}>
          Any project that has current terminations will be overwritten if selected. Read only projects can not be selected.
        </QESAlert>


        {wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost ?
          <fieldset>
            <div className="row">
              <div className="col">
                <p>Define what you wish to terminate on:</p>
                <div className="row">
                  <div className="col-4 pt-2">
                    <QESRadioGroup
                      renderInline={true}
                    >
                      <QESRadioButton
                        label="Ghost by LOB"
                        value="ghost"
                        name="termination-type"
                        checked={true}
                        testId="select_projects__termination-type--ghost"
                      />
                    </QESRadioGroup>
                  </div>
                  <div className="col-8 pr-5">
                    <QESSelect
                      className="bg-white"
                      containerClassName="mb-3"
                      label={null}
                      value={wizardContext.selectedLineOfBusiness}
                      isValid={!!wizardContext.selectedLineOfBusiness}
                      onChange={(e) => wizardContext.setSelectedLineOfBusiness(e.target.value)}

                      testId="ghost_termination__line_of_business">
                      {Object.values(ClaimsActivityLineOfBusinessTypeEnum)
                        .map((lineOfBusinessType, index) => (
                          <option
                            key={index}
                            value={lineOfBusinessType}
                          >
                            {lineOfBusinessType}
                          </option>
                        ))}
                    </QESSelect>
                  </div>
                </div>

              </div>
              <div className="col">
                <p>Additional Termination Refinement Options:</p>
                <div className="mt-n2">
                  <QESRadioGroup
                    renderInline={true}
                  >
                    <QESCheckbox
                      containerClassName="form-check mr-5"
                      label="All Locations"
                      checked={true}
                      className="form-check-input mt-0"
                      testId="select_projects__termination-refinement-type--all-locations"
                      disabled={true}
                    />
                    <QESCheckbox
                      containerClassName="form-check"
                      label="All Specialties"
                      checked={true}
                      className="form-check-input mt-0"
                      testId="select_projects__termination-refinement-type--all-specialties"
                      disabled={true}
                    />
                  </QESRadioGroup>
                </div>
              </div>
            </div>
            <SearchBox
              escapeSearchValue={true}
              fieldsetClass="d-flex"
              inputLabel="Search Projects"
              onSubmit={(value: string) => setProjectSearchString(value)}
              testId="ghost-terminations-search-projects"
            />
          </fieldset>
          :
          <SearchBox
            escapeSearchValue={true}
            fieldsetClass="d-flex"
            inputLabel="Search Projects"
            onSubmit={(value: string) => setProjectSearchString(value)}
            testId="import-terminations-search-projects"
          />
        }
      </div>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <Waffles<BulkProviderChangeProjectTableData>
              columns={ProjectsTableColumnConfig}
              data={renderedProjects}
              fullWidth={true}
              loading={wizardContext.projects.length === 0}
              handleCheckboxChange={handleCheckboxChange}
              selectedKeys={wizardContext.selectedProjectIds}
              showCheckboxes={true}
              testId="import-terminations-projects-table"
              totalNumberOfRecords={wizardContext.projects.length}
            />
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default TerminationsWizardSelectProjects;
